import {
  Fragment,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { Button, Stack, Typography } from '@mui/material';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import ScreenCenter from '@/components/wrapped/ScreenCenter';

import AccessVerifiedScreen from '@/karehero-app/onboarding/current-flow/AccessVerifiedScreen';
import AccessVerifiedConsentScreen from '@/karehero-app/tell-us-once/intake/AccessVerifiedConsentScreen';
import AiConsentScreen from '@/karehero-app/tell-us-once/intake/AiConsentScreen';
import AiConsentDeclinedScreen from '@/karehero-app/tell-us-once/intake/AiConsentDeclinedScreen';
import ForkScreen from '@/karehero-app/tell-us-once/intake/ForkScreen';
import WhoScreen from '@/karehero-app/tell-us-once/intake/WhoScreen';
import ChatV1 from '@/karehero-app/tell-us-once/intake/ChatV1';
import ChatV2 from '@/karehero-app/tell-us-once/intake/ChatV2';
import ChatDesktop from '@/karehero-app/tell-us-once/intake/ChatDesktop';
import LegalNameScreen from '@/karehero-app/tell-us-once/intake/LegalNameScreen';
import FilledScreen from '@/karehero-app/tell-us-once/intake/FilledScreen';
import ChooseNextScreen from '@/karehero-app/tell-us-once/intake/ChooseNextScreen';
import ProcessingScreen from '@/karehero-app/tell-us-once/intake/ProcessingScreen';
import ProcessingChatScreen from '@/karehero-app/tell-us-once/intake/ProcessingChatScreen';
import InAppHome from '@/karehero-app/tell-us-once/intake/InAppHome';
import ReviewFactsScreen from '@/karehero-app/tell-us-once/intake/ReviewFactsScreen';
import SummaryScreen from '@/karehero-app/tell-us-once/intake/SummaryScreen';
import IntakeActionsScreen from '@/karehero-app/tell-us-once/intake/IntakeActionsScreen';
import ReviewScreen from '@/karehero-app/tell-us-once/intake/ReviewScreen';
import ReviewAllScreen from '@/karehero-app/tell-us-once/intake/ReviewAllScreen';
import CareProfileScreen from '@/karehero-app/tell-us-once/intake/CareProfileScreen';
import CalculatorScreen from '@/karehero-app/tell-us-once/intake/CalculatorScreen';
import EligibleScreen from '@/karehero-app/tell-us-once/intake/EligibleScreen';
import SelfBranchScreen from '@/karehero-app/tell-us-once/intake/SelfBranchScreen';
import BookCallScreen from '@/karehero-app/tell-us-once/intake/BookCallScreen';
import CarriedScreen from '@/karehero-app/tell-us-once/benefits/CarriedScreen';
import DocumentsReadScreen from '@/karehero-app/tell-us-once/benefits/DocumentsReadScreen';
import ResultScreen from '@/karehero-app/tell-us-once/benefits/ResultScreen';
import CalculatorBar from '@/karehero-app/tell-us-once/benefits/CalculatorBar';
import {
  CALCULATOR_CHAT,
  CALCULATOR_KEEP_ANSWERS,
} from '@/karehero-app/tell-us-once/benefits/BenefitsCalculator.content';
import {
  CARER_INITIALS,
  INTAKE_BAR,
  INTAKE_CHAT,
  INTAKE_CHAT_AGAIN,
  INTAKE_CHAT_V2,
  INTAKE_CHAT_V2_SELF,
  INTAKE_CHAT_V2_WITH_FOLLOW_UP,
  INTAKE_FORK_ANSWERS,
  INTAKE_PUSHBACK,
  RECIPIENT,
  REVIEW_BAR,
  intakeStepIn,
  type ChatQuestion,
} from '@/karehero-app/tell-us-once/intake/content';
import {
  AI_CONSENT_V1_1,
  INTAKE_CHAT_V1_1,
  INTAKE_SUMMARY_V1_1,
  PROCESSING_DONE_V1_1,
  PROCESSING_OPENING_V1_1,
  PROCESSING_WORK_V1_1,
  YOURE_IN_V1_1,
} from '@/karehero-app/tell-us-once/intake/content.v1-1';
import ChatHistoryDrawer from '@/karehero-app/tell-us-once/intake/ChatHistoryDrawer';
import HomeConcept2 from '@/karehero-app/tell-us-once/intake/HomeConcept2';
import {
  INTAKE_CHAT_V2_0_AA,
  INTAKE_CHAT_V2_0_AA_ALL,
  HOME_TYPED,
  INTAKE_CHAT_V2_0_HOME_SENT,
  INTAKE_CHAT_V2_0_HOME_UNSURE,
  HOME_GREETING,
  INTAKE_CHAT_V2_0,
  INTAKE_CHAT_V2_0_HOME,
  INTAKE_CHAT_V2_0_LATER,
} from '@/karehero-app/tell-us-once/intake/content.v2-0';
import {
  INTAKE_CHAT_V2_0_DEV,
  INTAKE_CHAT_V2_0_DEV_BRANCHES,
  INTAKE_CHAT_V2_0_DEV_CORRECTED,
  INTAKE_CHAT_V2_0_DYNAMIC,
  OWN_WORDS_LOCATION,
  type IntakeDevBranch,
  LIVE_CAPTION_SCRIPT,
  LIVE_CAPTION_SCRIPT_MISSING,
  LIVE_CAPTION_SCRIPT_SELF,
  HELP_STATES,
  HELP_TYPED,
  BOOK_CALL,
  PHONE_PUSHBACK,
} from '@/karehero-app/tell-us-once/intake/content.v2-0-dev';
import { asV21 } from '@/karehero-app/tell-us-once/intake/content.v2-1';
import {
  FlowVersionLayer,
  type FlowVersion,
} from '@/karehero-app/tell-us-once/intake/flowVersion';
import {
  INTAKE_STYLE,
  IntakeStyleContext,
  ONBOARDING_2_0_STYLE,
} from '@/karehero-app/tell-us-once/intake/IntakeStyle';

/** Concept 3 and its 3.1 scenes: which conversation, and where in it. */
const HOME_C3: Partial<
  Record<
    Frame,
    {
      questions: ChatQuestion[];
      startAt?: number;
      typed?: string;
      keyboard?: boolean;
    }
  >
> = {
  'home-concept-2-todo': { questions: INTAKE_CHAT_V2_0_HOME },
  'home-c3-typing': {
    questions: INTAKE_CHAT_V2_0_HOME_SENT,
    typed: HOME_TYPED,
    keyboard: true,
  },
  'home-c3-sent': { questions: INTAKE_CHAT_V2_0_HOME_SENT, startAt: 1 },
  'home-c3-unsure': { questions: INTAKE_CHAT_V2_0_HOME_UNSURE, startAt: 1 },
  'home-c3-put-away': { questions: INTAKE_CHAT_V2_0_HOME_UNSURE, startAt: 2 },
};

/** The dev branch a frame opens on when Flow is not given one. */
const FRAME_BRANCH: Partial<Record<Frame, IntakeDevBranch>> = {
  'help-not-quite': 'not-quite',
  'help-summary-fixed': 'not-quite',
  'help-how-scotland': 'scotland',
  'help-how-younger': 'younger',
  'help-how-no-daily-help': 'no-daily-help',
  'help-own-words-answered': 'own-words',
  'help-not-now': 'not-now',
  'help-later': 'later',
  'help-under-18': 'under-18',
  'help-phone-later': 'phone-later',
  'help-need-help': 'need-help',
  'help-need-help-first-summary': 'need-help',
  'help-need-help-reading': 'need-help',
  'help-need-help-summary': 'need-help',
  // The parked middle: off the main path for now, so these walk the whole
  // journey to reach their turn.
  'help-how': 'full',
  'help-how-yes': 'full',
  'help-need': 'full',
  'help-gather': 'full',
  'help-gathered': 'full',
  'help-gather-done': 'full',
  'help-collected': 'full',
  'help-collected-editing': 'full',
  'help-change-answer': 'full',
  'help-change-answer-done': 'full',
};

/** The question each branch frame opens on. */
const BRANCH_STEP: Partial<Record<Frame, string>> = {
  'help-not-quite': 'not-quite',
  'help-summary-fixed': 'summary-fixed',
  'help-how-scotland': 'how-we-help',
  'help-how-younger': 'how-we-help-younger',
  'help-how-no-daily-help': 'how-we-help',
  // Where she lives said in their own words, age with it: on to how we can
  // help, the age question skipped.
  'help-own-words-answered': 'how-we-help',
  // Not right now carries on, so it opens on what we'd need after it.
  'help-not-now': 'what-we-need',
  // I'll do it later skips sending it, so it opens on where to start.
  'help-later': 'start',
  // Under 18 picked, so it opens on the kind stop.
  'help-under-18': 'under-18',
  // The phone number left for later, so it opens on where to start after it.
  'help-phone-later': 'start',
  // Shown only when the first answer left out what they need help with: the
  // summary without it, then the question that asks it.
  'help-need-help-first-summary': 'summary',
  'help-need-help': 'need-help',
};

// New each time this module runs again on a hot reload. See the end of Flow.
const LOADED_AT = Date.now();

/** You're talking to Keni, over the 2.0 dev chat. Off for now. */
const KENI_INTRO = false;

// Option one: sign-up lands on You're in, the two opening onboarding questions
// establish who we are helping, then the conversation opens.
//
// One id per screen so the flows page can render any of them on its own.
export type Frame =
  | 'youre-in'
  // Option B's You're in: the same screen, continuing to the AI consent on a
  // screen of its own rather than into the chat.
  | 'youre-in-b'
  | 'youre-in-consent'
  | 'ai-consent'
  | 'ai-consent-declined'
  | 'supporting'
  | 'caring-for'
  | 'chat'
  | 'legal-name'
  | 'chat-v2'
  | 'chat-v2-fork'
  | 'chat-v2-chosen'
  | 'chat-v2-chosen-self'
  | 'chat-v2-who'
  | 'chat-v2-who-photo'
  | 'chat-v2-who-photo-uploading'
  | 'fail-upload-photo'
  | 'chat-v2-who-self'
  | 'self-branch'
  | 'chat-v2-situation'
  | 'chat-v2-situation-photo'
  | 'chat-v2-recording'
  | 'chat-v2-voice'
  | 'chat-v2-anything-more'
  | 'chat-v2-anything-more-added'
  | 'chat-v2-more'
  | 'chat-v2-more-doc'
  | 'chat-v2-more-doc-uploading'
  | 'fail-upload-doc'
  | 'chat-v2-attach'
  | 'chat-v2-stuck'
  | 'chat-desktop'
  | 'processing'
  | 'processing-in-app'
  | 'chat-v2-carer-name'
  | 'chat-v2-preferred-name'
  | 'chat-v2-mobile'
  | 'fail-mobile'
  | 'chat-v2-reading'
  | 'fail-voice'
  | 'fail-nothing'
  | 'fail-send'
  | 'fail-send-voice'
  | 'fail-one-name'
  | 'choose-next'
  | 'filled'
  | 'calculator'
  | 'eligible'
  | 'review'
  | 'book-call'
  | 'review-all'
  | 'review-facts'
  | 'review-facts-kept'
  | 'summary'
  | 'summary-editing'
  | 'summary-problem'
  | 'chat-v2-again'
  | 'chat-v2-again-answered'
  | 'review-facts-read'
  | 'review-facts-gap'
  | 'review-facts-conflict'
  | 'review-facts-stale'
  | 'intake-actions'
  | 'care-profile-unreviewed'
  | 'care-profile-arriving'
  | 'care-profile'
  | 'home'
  | 'help-ask'
  | 'help-ask-fresh'
  | 'old-onboarding'
  | 'consent-explain'
  | 'keni-about'
  | 'help-recording'
  | 'help-recording-captions'
  | 'help-processing'
  | 'help-summary'
  | 'help-summary-recording'
  | 'help-summary-starts-recording'
  | 'help-summary-typing'
  | 'help-summary-correcting'
  | 'help-summary-pencil'
  | 'help-summary-voice-reading'
  | 'help-summary-corrected'
  | 'help-gather-done'
  | 'help-how-yes'
  | 'help-ask-starts-recording'
  | 'help-collected'
  | 'help-still-need'
  | 'help-collected-editing'
  | 'help-questions'
  | 'help-questions-open'
  | 'help-age'
  | 'help-age-answered'
  | 'help-start-picking'
  | 'help-how'
  | 'help-need'
  | 'help-gather'
  | 'help-gathered'
  | 'help-start'
  | 'help-phone'
  | 'help-phone-short'
  | 'help-phone-long'
  | 'help-phone-later'
  | 'help-need-help'
  | 'help-need-help-first-summary'
  | 'help-need-help-reading'
  | 'help-need-help-summary'
  | 'help-under-18'
  | 'help-start-talk'
  | 'book-call-placeholder'
  | 'home-placeholder'
  | 'ai-policy-placeholder'
  | 'help-eligible'
  | 'help-not-quite'
  | 'help-summary-fixed'
  | 'help-how-scotland'
  | 'help-how-younger'
  | 'help-how-no-daily-help'
  | 'help-own-words-typed'
  | 'help-own-words-answered'
  | 'help-change-answer'
  | 'help-change-answer-done'
  | 'help-not-now'
  | 'help-later'
  | 'help-typed'
  | 'help-photo'
  | 'help-fail-send-voice'
  | 'help-fail-send'
  | 'help-fail-upload'
  | 'help-mic-blocked'
  | 'help-not-understood'
  | 'help-too-short'
  | 'help-resumed'
  | 'home-conversations'
  | 'home-conversations-menu'
  | 'home-concept-2'
  | 'home-concept-2-todo'
  | 'home-c3-typing'
  | 'home-c3-sent'
  | 'home-c3-unsure'
  | 'home-c3-put-away'
  | 'home-c4'
  | 'home-c4-ask'
  | 'home-c4-ask-lime'
  | 'home-c4-aa'
  | 'home-c4-aa-part'
  | 'home-c4-aa-all'
  | 'help-start-later'
  | 'calc-start'
  | 'calc-paperwork'
  | 'calc-read'
  | 'calc-questions'
  | 'calc-result';

// Where the calculator's questions start, after the paperwork turn and the
// reading. By id, so adding a turn at the front cannot move it silently.
const CALC_QUESTIONS_AT = CALCULATOR_CHAT.findIndex(
  (q) => q.id === 'confirm-location',
);

// How long the reading turn holds before the read-back. Long enough to see the
// dots; the real reading is a few seconds.
const CALC_READING_MS = 2400;

// How long 2.0's reading holds before the summary, in the prototype.

/** 1 asks the opening questions on their own screens, 2 asks them in the chat. */
export type Option = 1 | 2;

const Flow = ({
  initial = 'youre-in',
  option = 1,
  onFrameChange,
  onChatQuestion,
  sheetOpen,
  onReplay,
  version: asked = '1.0',
  branch,
}: {
  /**
   * Which onboarding. 1.1 is the same flow warmed up (content.v1-1.ts): the
   * same questions in the same order, with warmer words and things given back.
   * 2.1 is dev's 2.0 with the turns content.v2-1.ts changes.
   */
  version?: FlowVersion;
  /**
   * Off the happy path. `missing`: on dev, their first answer leaves out what
   * they need help with, so the prompts card is not all ticked.
   */
  branch?: 'missing' | 'corrected' | IntakeDevBranch;
  initial?: Frame;
  option?: Option;
  /**
   * Handed the chat's way to replay the bottom bar's change. Passed to every
   * chat frame rather than switched on per frame: which screens offer it is the
   * stage's call, not the flow's.
   */
  onReplay?: (replay: () => void) => void;
  /**
   * Which screen the flow is on now. A stage passes this so its screen pill
   * follows the prototype as it is clicked through, rather than only saying
   * where the reader started.
   */
  onFrameChange?: (frame: Frame) => void;
  /**
   * Which question the chat is on, for the same reason: the conversation is a
   * dozen screens inside one frame, so a stage tracking frames alone would sit
   * on the frame's name while the reader answered their way past it.
   */
  onChatQuestion?: (id: string) => void;
  /**
   * Draw the screen with the account sheet already open.
   *
   * A harness prop, for the Masters pages: the sheet is behind a tap, so a page
   * that only renders the screen shows an avatar and leaves what it does to
   * whoever thinks to press it.
   */
  sheetOpen?: boolean;
}) => {
  // A flows page showing a minor version over its major's screens asks for the
  // major, and the layer around it says which version that now means.
  const layer = useContext(FlowVersionLayer);
  const version = layer && asked === layer.from ? layer.to : asked;
  // 2.1 is built on dev's 2.0, so everything dev does, it does.
  const dev = version === '2.0-dev' || version === '2.1';
  const [frame, setFrame] = useState<Frame>(initial);
  // Where the calculator goes back to: Home, or the Home concept it was
  // opened from.
  const [calcHome, setCalcHome] = useState<Frame>('home');
  // The chat Home's conversations drawer.
  const [historyOpen, setHistoryOpen] = useState(
    initial === 'home-conversations-menu',
  );
  // A branch's screens open on their branch without being told, so the
  // prototype's screen list can hold them too.
  const path = branch ?? FRAME_BRANCH[initial];
  // 2.0 is built on 1.1, so it keeps 1.1's words and give-backs.
  const warm = version !== '1.0';
  // 2.0-dev is 2.0 as built on dev: the same design, with the build's changes.
  const is20 = version === '2.0' || dev;
  const devQs =
    path === 'corrected'
      ? INTAKE_CHAT_V2_0_DEV_CORRECTED
      : path && path !== 'missing'
        ? INTAKE_CHAT_V2_0_DEV_BRANCHES[path]
        : INTAKE_CHAT_V2_0_DEV;
  const chatQs = dev
    ? version === '2.1'
      ? // Consent to share is never asked of someone caring for themselves.
        asV21(devQs, path !== 'self' && path !== 'self-full')
      : devQs
    : version === '2.0'
      ? INTAKE_CHAT_V2_0
      : warm
        ? INTAKE_CHAT_V1_1
        : INTAKE_CHAT_V2;
  // Where a question sits in this version's chat. 2.0 asks one more first.
  const step = (id: string) => intakeStepIn(chatQs, id);
  // The dynamic flow's own ending, once what we've got is confirmed. Dev's
  // ends on where to start instead.
  const dynamicStep = (id: string) =>
    intakeStepIn(INTAKE_CHAT_V2_0_DYNAMIC, id);

  // Reported from an effect rather than from each of the seventy setFrame calls,
  // so a new screen cannot be wired up and forget to announce itself.
  useEffect(() => {
    onFrameChange?.(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frame]);

  // The app bar every screen in the intake carries. The account exists from
  // "You're in" onwards and Home does not, so the avatar is the only route to
  // Help and Sign out across the whole flow.
  //
  // Two of them, split at the point the chat gets their name: before it the
  // avatar is a person icon, after it their initials. Made out of the same
  // answer the transcript shows, so the bar cannot name a different person.
  //
  // A third for what comes after them. The questions are somewhere you are, so
  // their bar is named and drops the logo; once they are answered you are in
  // the app, and the app's bar is the app's own.
  const signOut = {
    onSignOut: () => setFrame('youre-in'),
    initialSheetOpen: sheetOpen,
  };
  const account = { ...signOut, label: INTAKE_BAR };
  const named = { ...account, initials: CARER_INITIALS };
  // In the app the sheet is the settings menu proper: your details and the care
  // circle sit above Help, because by now there is an account with something in
  // it to change.
  const app = { ...signOut, initials: CARER_INITIALS, inApp: true };
  // Reading back what it heard is its own stretch, between the asking and the
  // app: the questions are over, the profile is not open yet.
  const review = { ...signOut, initials: CARER_INITIALS, label: REVIEW_BAR };

  // Finishing the conversation, from wherever in it you were. Every chat frame
  // is the same conversation at a different point, so they all end the same
  // way: the reading, and the chat that carries on over it.
  //
  // Twenty-five of them jumped to the filled screen instead, which skipped both
  // the reading and the review. One of them was fixed by hand and the other
  // twenty-four still went the old way, which is exactly the failure a shared
  // handler stops.
  // 2.0's conversation ends on the summary for now, so nothing follows it.
  const doneChatting = () => {
    if (!is20) setFrame('processing');
  };

  // The calculator's paperwork turn ends on the reading, which the chat has no
  // way on from: it is a message, not a question. The flow moves it on to the
  // read-back once it has been on screen for a beat, and only while it is.
  const [calcReading, setCalcReading] = useState(false);
  useEffect(() => {
    if (!calcReading || frame !== 'calc-paperwork') return;
    const t = window.setTimeout(() => {
      setCalcReading(false);
      setFrame('calc-read');
    }, CALC_READING_MS);
    return () => window.clearTimeout(t);
  }, [calcReading, frame]);

  // The calculator's bar. Its X leaves to Home, which is where it was opened.
  const calcBar = <CalculatorBar onLeave={() => setFrame(calcHome)} />;

  // Spread into every chat below, so a new chat frame reports its questions
  // without anyone remembering to wire it.
  const chat = {
    onQuestionChange: onChatQuestion,
    onLeave: () => setFrame('old-onboarding'),
    onReplay,
    // 2.0 opens on the logo rather than a section name: the first question is
    // about them as a whole, not one set of questions among several.
    account: is20 ? signOut : account,
    // 2.0 records from the button beside the field, as WhatsApp does.
    inlineMic: is20,
    // Live captions are on dev only, so far.
    liveCaptions: dev
      ? (path === 'missing'
          ? LIVE_CAPTION_SCRIPT_MISSING
          : path === 'self'
            ? LIVE_CAPTION_SCRIPT_SELF
            : LIVE_CAPTION_SCRIPT
        ).split(' ')
      : undefined,
    // Dev opens on who they are talking to, before Keni's first message.
    // Hidden for now: flip KENI_INTRO back on to bring it, and the Keni the
    // llama sheet it opens, back.
    intro:
      KENI_INTRO && dev
        ? {
            title: "You're talking to Keni",
            about: {
              title: 'Keni the llama',
              points: [
                'Llamas are pack animals. They live in herds, look out for each other, and share the load.',
                'Caring for someone is a heavy load too, and nobody should have to carry it on their own.',
                'Keni is part of your pack: here to help you work out what support you can get, and to bring in our care experts when you need them.',
              ],
            },
          }
        : undefined,
    // Without what they need help with, the last prompt is not found.
    missed: path === 'missing' ? 1 : 0,
    // Where 2.0 ends. On dev, Book a call on where to start goes to the book a
    // call placeholder, and Do this later to the home placeholder; the dynamic
    // concept goes to a Home that carries the conversations on. A chat frame
    // that passes its own onComplete after the spread still wins.
    onComplete: is20
      ? (answers: string[]) =>
          setFrame(
            answers.includes(BOOK_CALL)
              ? 'book-call-placeholder'
              : dev
                ? 'home-placeholder'
                : 'home-conversations',
          )
      : undefined,
    namedFrom: 'carer-preferred-name',
    // Read the full AI policy, in How we use AI: out to where it is hosted.
    onExplainLink: () => setFrame('ai-policy-placeholder'),
  };

  // The frame being shown, drawn inside its version's style sheet below.
  const screen = (): ReactNode => {
    switch (frame) {
      // On dev, straight into the chat, where the consent is asked: option A,
      // the one being built. Earlier versions keep their consent screen.
      case 'youre-in':
        return (
          <AccessVerifiedScreen
            account={signOut}
            onContinue={() => setFrame(dev ? 'chat-v2' : 'ai-consent')}
            {...(warm ? { description: YOURE_IN_V1_1 } : {})}
          />
        );

      // Option B, only when picked: the consent on its own screen first.
      case 'youre-in-b':
        return (
          <AccessVerifiedScreen
            account={signOut}
            onContinue={() => setFrame('ai-consent')}
            {...(warm ? { description: YOURE_IN_V1_1 } : {})}
          />
        );

      // The same arrival with the consent folded into it, so Continue is the
      // whole of getting in. It skips 'ai-consent' and lands on the first
      // question, which is the combination it is testing.
      case 'youre-in-consent':
        return (
          <AccessVerifiedConsentScreen
            account={signOut}
            onContinue={() => setFrame(option === 2 ? 'chat-v2' : 'supporting')}
          />
        );

      // The consent sits between access and the first question, so it is where the
      // option split now happens: whichever shape the questions take, they are
      // read by a model and nothing is asked before this is answered.
      case 'ai-consent':
        return (
          // The logo bar, as on You're in: the questions have not started, so
          // there is no set of them for the bar to name.
          <AiConsentScreen
            account={signOut}
            {...(warm ? { content: AI_CONSENT_V1_1 } : {})}
            // On 2.0 dev, the consent already given here: the chat opens fresh
            // on the first question rather than asking it again.
            onAccept={() =>
              setFrame(
                dev
                  ? 'help-ask-fresh'
                  : option === 2
                    ? 'chat-v2'
                    : 'supporting',
              )
            }
            onDecline={() => setFrame('ai-consent-declined')}
          />
        );

      case 'ai-consent-declined':
        return <AiConsentDeclinedScreen />;

      case 'chat-v2':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The fork, unanswered, with the two name questions answered above it. The
      // chat opens on the carer's name now, so a scene that says "Are you
      // supporting someone?" has to say where in the conversation that is rather
      // than starting the chat and hoping.
      case 'chat-v2-fork':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('fork')}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The same question with its first answer already chosen, so a flows page
      // can show the choice without the reader having to make it.
      case 'chat-v2-chosen':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('fork')}
            chose={INTAKE_FORK_ANSWERS[0]}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      case 'chat-v2-chosen-self':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('fork')}
            chose={INTAKE_FORK_ANSWERS[1]}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      case 'chat-v2-who':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('who')}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      case 'chat-v2-who-self':
        return (
          <ChatV2
            {...chat}
            questions={INTAKE_CHAT_V2_SELF}
            startAt={step('who')}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      case 'self-branch':
        return <SelfBranchScreen />;

      case 'chat-v2-situation':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('situation')}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // A photo chosen from the plus, sitting in the composer to be looked at
      // before it goes. Send puts it in the thread; the cross takes it back.
      case 'chat-v2-situation-photo':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('situation')}
            staged
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // Mid-recording: the orb is listening and the clock is running. What it
      // turns into is the screen after this one.
      case 'chat-v2-recording':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('situation')}
            recording
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      case 'chat-v2-voice':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('situation')}
            recorded={47}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The confirm that closes the capture: what they sent, then the ask, then
      // the two answers. "I'm done" is what starts the reading.
      case 'chat-v2-anything-more':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('anything-more')}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // They kept going: two voice notes, a photo and another note since the ask.
      // Each one landed above "I'm done", so it is still the last thing there.
      case 'chat-v2-anything-more-added':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('anything-more')}
            sent={2}
            uploaded='photo'
            sentAfter={1}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The previous version of the capture: the situation question followed by a
      // follow-up that stayed open and was left with "Continue when you're done".
      // Kept reachable so the one-message shape can be read against it; it is the
      // only frame that mounts the old question list.
      case 'chat-v2-more':
        return (
          <ChatV2
            {...chat}
            questions={INTAKE_CHAT_V2_WITH_FOLLOW_UP}
            startAt={intakeStepIn(
              INTAKE_CHAT_V2_WITH_FOLLOW_UP,
              'anything-else',
            )}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      case 'chat-v2-carer-name':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('carer-name')}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The beat between their answer and the reply, held open. In the flow it
      // lasts 900ms and then the next question lands on top of it, so it is a
      // pattern nobody can look at without stopping it here.
      case 'chat-v2-reading':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('carer-name')}
            replied={INTAKE_CHAT_V2[step('carer-name')].answer}
            reading
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      case 'chat-v2-preferred-name':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('carer-preferred-name')}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      case 'chat-v2-mobile':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('carer-mobile')}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The plus, open. What it offers is the two things this flow takes.
      case 'chat-v2-attach':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('situation')}
            attaching
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // Five notes in, the way on still there. Continue goes where it goes from
      // any other version of this question: the reading, and the conversation
      // that carries on over it. It used to jump to the filled screen, which
      // skipped the two screens that moment leads to and made this the one scene
      // you could not walk end to end from.
      case 'chat-v2-stuck':
        return (
          <ChatV2
            {...chat}
            questions={INTAKE_CHAT_V2_WITH_FOLLOW_UP}
            startAt={intakeStepIn(
              INTAKE_CHAT_V2_WITH_FOLLOW_UP,
              'anything-else',
            )}
            sent={5}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      case 'supporting':
        return (
          <ForkScreen
            account={account}
            onNext={() => setFrame('caring-for')}
            onBack={() => setFrame('youre-in')}
          />
        );

      case 'caring-for':
        return (
          <WhoScreen
            account={account}
            onNext={() => setFrame('chat')}
            onBack={() => setFrame('supporting')}
          />
        );

      case 'chat':
        return (
          <ChatV1
            questions={INTAKE_CHAT}
            onComplete={() => setFrame('legal-name')}
            onExit={() => setFrame('caring-for')}
          />
        );

      // Answered by handing something over rather than typing it.
      case 'chat-v2-who-photo':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('who')}
            uploaded='photo'
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // Mid upload: the file leaving the phone. What it turns into comes later, at
      // the pause.
      case 'chat-v2-who-photo-uploading':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('who')}
            uploaded='photo'
            uploadState='uploading'
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      case 'chat-v2-more-doc-uploading':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('anything-more')}
            sent={1}
            uploaded='document'
            uploadState='uploading'
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      case 'chat-v2-more-doc':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('anything-more')}
            sent={1}
            uploaded='document'
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The transfer failed, so there is nothing to read yet. Distinct from
      // fail-upload, which is a photo that arrived and could not be read.
      case 'fail-upload-photo':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('who')}
            uploaded='photo'
            uploadState='failed'
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      case 'fail-upload-doc':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('anything-more')}
            sent={1}
            uploaded='document'
            uploadState='failed'
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // Both audio failures sit on the question that holds the way on, with the
      // way on spent. See the note in the spec: with processing moved to the end of
      // the intake, where they surface is an open question.
      case 'fail-voice':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('situation')}
            sent={3}
            processed
            failure='voice'
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      case 'fail-nothing':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('situation')}
            sent={5}
            processed
            failure='nothing'
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The same failure, spoken. A voice note draws nothing like a bubble and
      // fails the same way, so the treatment wraps it rather than living in it.
      case 'fail-send-voice':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('situation')}
            sent={1}
            sendFailed
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // Not an alert: the message that did not leave the phone carries it.
      case 'fail-send':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('situation')}
            replied='She has been getting more confused since the fall.'
            sendFailed
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // A short answer rather than a broken one: they gave a first name, so the
      // question comes back asking for the rest.
      // A number that is not a mobile, said back. The question stays open.
      case 'fail-mobile':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('carer-mobile')}
            replied='0770 090'
            pushback={INTAKE_PUSHBACK.mobileShort}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // 2.0's phone number, typed too short or too long: said back, the
      // question open.
      case 'help-phone-short':
      case 'help-phone-long':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('phone')}
            replied={
              frame === 'help-phone-short' ? '0770 090' : '07700 900461 22'
            }
            pushback={
              frame === 'help-phone-short'
                ? PHONE_PUSHBACK.short
                : PHONE_PUSHBACK.long
            }
            onExit={() => setFrame('youre-in')}
          />
        );

      case 'fail-one-name':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('who')}
            replied={RECIPIENT}
            pushback={INTAKE_PUSHBACK.oneName}
            onComplete={doneChatting}
            onExit={() => setFrame('youre-in')}
          />
        );

      // Everything they sent, read at once, at the end. Nothing waits on the
      // person and the person does not wait on us.
      // The reading, in the conversation they have been in the whole time. The
      // notice arrives over it when the reading is done, and that is the way on
      // to the review: a screen of its own to say the conversation is over ends
      // the conversation to announce something about it.
      case 'processing':
        return (
          <ProcessingChatScreen
            account={named}
            {...(warm
              ? {
                  opening: PROCESSING_OPENING_V1_1,
                  done: PROCESSING_DONE_V1_1,
                  work: PROCESSING_WORK_V1_1,
                }
              : {})}
            notice
            // Long, because this one gets demonstrated. The notice arrives when
            // the conversation comes to rest anyway, so this only governs a
            // screen somebody is talking over rather than clicking through.
            noticeAfter={45000}
            // Into the review flow, which starts on "Does this look right?".
            // review-facts is one screen out of it rather than its front door.
            onContinue={() => setFrame('summary')}
          />
        );

      // The same message with the app around it, so they can get on with something
      // else while we read it.
      case 'processing-in-app':
        return <ProcessingScreen account={app} inApp />;

      // The same conversation, off the phone.
      case 'chat-desktop':
        return <ChatDesktop />;

      // Where it all ends: the app, on Home, with the tab bar under it.
      case 'home':
        return <InAppHome onCalculator={() => setFrame('calc-start')} />;

      // Onboarding 2.0: what they need help with, being read, held on the beat
      // between their answer and the summary. In the chat, the reply bubble
      // says what is happening a line at a time.
      // Where I'd rather not on dev's consent goes: the onboarding without the
      // AI. A placeholder until that is pulled in.
      case 'old-onboarding':
        return (
          <ScreenRoot>
            <ScreenCenter>
              <Stack sx={{ textAlign: 'center' }} spacing={1}>
                <Typography variant='h3'>Old-style onboarding</Typography>
                <Typography sx={{ color: 'text.secondary' }} variant='body2'>
                  Placeholder. Where I'd rather not goes: the onboarding without
                  the AI.
                </Typography>
              </Stack>
            </ScreenCenter>
          </ScreenRoot>
        );

      // Where Book a call with a care expert goes, on where to start.
      // A placeholder until that screen is designed for 2.0.
      // Where every other answer on where to start goes: into the app. A
      // placeholder too, until 2.0's home is decided.
      case 'book-call-placeholder':
      case 'home-placeholder':
        return (
          <ScreenRoot>
            <ScreenCenter>
              <Stack sx={{ textAlign: 'center' }} spacing={1}>
                <Typography variant='h3'>
                  {frame === 'home-placeholder'
                    ? 'Home page'
                    : 'Book a call page'}
                </Typography>
                <Typography sx={{ color: 'text.secondary' }} variant='body2'>
                  Placeholder.
                </Typography>
              </Stack>
            </ScreenCenter>
          </ScreenRoot>
        );

      // Read the full AI policy, tapped in How we use AI: the page it opens,
      // outside the app, wherever the policy ends up hosted. Back returns to
      // the sheet it came from.
      case 'ai-policy-placeholder':
        return (
          <ScreenRoot>
            <ScreenCenter>
              <Stack
                sx={{ textAlign: 'center', alignItems: 'center' }}
                spacing={2}
              >
                <Stack spacing={1}>
                  <Typography variant='h3'>AI policy</Typography>
                  <Typography sx={{ color: 'text.secondary' }} variant='body2'>
                    Placeholder. Opens outside the app, on our website or
                    wherever the AI policy is hosted.
                  </Typography>
                </Stack>
                <Button
                  variant='outlined'
                  onClick={() => setFrame('consent-explain')}
                >
                  Back to the chat
                </Button>
              </Stack>
            </ScreenCenter>
          </ScreenRoot>
        );

      // The open ask with nothing above it: the consent was its own screen
      // before the chat (You're in, then Before we start), so the chat starts
      // here.
      case 'help-ask-fresh':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('help')}
            fresh
            onExit={() => setFrame('youre-in')}
          />
        );

      // The open ask, wherever it sits. On dev the consent comes before it.
      case 'help-ask':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('help')}
            onExit={() => setFrame('youre-in')}
          />
        );

      // Dev's opening, with Keni's picture tapped: why a llama.
      case 'keni-about':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            aboutKeniOpen
            onExit={() => setFrame('youre-in')}
          />
        );

      // Dev's opening, with How we use AI tapped: the detail in a sheet.
      case 'consent-explain':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            explainOpen='How we use AI'
            onExit={() => setFrame('youre-in')}
          />
        );

      // The first question, mid-recording: the clock running, and on dev the
      // CC switch in the row, off.
      case 'help-recording':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('help')}
            recording
            onExit={() => setFrame('youre-in')}
          />
        );

      // The same with the captions switched on: what they are saying, in a
      // card above the bar as they say it.
      case 'help-recording-captions':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('help')}
            recording
            captionsShown
            onExit={() => setFrame('youre-in')}
          />
        );

      case 'help-processing':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('help')}
            sent={1}
            reading
            // Carried on from recording with the captions on, so on dev the
            // note arrives with its words showing.
            captionsShown
            onExit={() => setFrame('youre-in')}
          />
        );

      // 2.0's summary, back in the chat: their answer above it, the summary as a
      // card, and checking it as rows. What either answer leads to is not
      // designed yet, so the conversation just ends there for now.
      case 'help-summary':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('summary')}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The summary, before it is confirmed, with them recording more: on dev,
      // with something still missing, the prompts card pins itself again.
      case 'help-summary-recording':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('summary')}
            recording
            captionsShown
            onExit={() => setFrame('youre-in')}
          />
        );

      // The same, starting to record a beat after it opens, so the card can be
      // watched coming back. Captions off, so only the bar changes under it.
      case 'help-summary-starts-recording':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('summary')}
            autoRecord
            onExit={() => setFrame('youre-in')}
          />
        );

      // 2.0 as built on dev, after "Yes, that's right": the triage questions
      // the summary left, before how we can help.
      case 'help-questions':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('location')}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The same, with See options tapped: five answers are too many to attach
      // as it arrives, so they open in the chat once they have read it.
      case 'help-questions-open':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('location')}
            optionsOpen
            onExit={() => setFrame('youre-in')}
          />
        );

      // The second triage question: four answers, attached under the ask.
      case 'help-age':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('age')}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The same, answered: the rest have folded away, leaving the one picked.
      case 'help-age-answered':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('age')}
            replied='66 or over'
            onExit={() => setFrame('youre-in')}
          />
        );

      // 2.0, after "Yes, that's right": how we can help, in the chat.
      case 'help-how':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('how-we-help')}
            onExit={() => setFrame('youre-in')}
          />
        );

      // 2.0, once they say they want help: what we would need from them.
      case 'help-need':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('what-we-need')}
            onExit={() => setFrame('youre-in')}
          />
        );

      // 2.0, after "Yes": handing over what we need, until they are done.
      case 'help-gather':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('gather')}
            onExit={() => setFrame('youre-in')}
          />
        );

      // 2.0, partway through handing it over: a voice note, a photo of a
      // letter and another voice note. The last is still being read, so two
      // are ticked on the pinned list.
      case 'help-gathered':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('gather')}
            sent={1}
            uploaded='photo'
            sentAfter={1}
            readingLast
            onExit={() => setFrame('youre-in')}
          />
        );

      // The summary with a correction typed into the field, not sent yet.
      case 'help-summary-typing':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('summary')}
            typed="It's forty, not fourteen"
            onExit={() => setFrame('youre-in')}
          />
        );

      // The correction sent, and being read.
      case 'help-summary-correcting':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('summary')}
            replied="It's forty, not fourteen"
            reading
            onExit={() => setFrame('youre-in')}
          />
        );

      // Where she lives, typed in their own words instead of tapped, not sent
      // yet. Her age is in it too.
      case 'help-own-words-typed':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('location')}
            typed={OWN_WORDS_LOCATION}
            onExit={() => setFrame('youre-in')}
          />
        );

      // Concept, not in dev: an earlier answer tapped to change it, its rows
      // in a sheet, as the benefits calculator does.
      case 'help-change-answer':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('how-we-help')}
            keepAnswers={CALCULATOR_KEEP_ANSWERS}
            changeAt={{ at: step('location'), open: true }}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The same, changed: the new answer in place, and a line saying so.
      case 'help-change-answer-done':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('how-we-help')}
            keepAnswers={CALCULATOR_KEEP_ANSWERS}
            changeAt={{ at: step('location'), to: 'Wales' }}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The pencil on the summary tapped instead of Not quite: its lines in
      // the composer to change, and a bar above saying so.
      case 'help-summary-pencil':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('summary')}
            editingCardOpen='Does this look right?'
            onExit={() => setFrame('youre-in')}
          />
        );

      // A new voice note sent on the summary instead of Not quite, with what
      // they got wrong in it, and being read.
      case 'help-summary-voice-reading':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('summary')}
            sent={1}
            readingLast
            onExit={() => setFrame('youre-in')}
          />
        );

      // The summary back with the correction in it, to confirm again.
      case 'help-summary-corrected':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('summary-corrected')}
            onExit={() => setFrame('youre-in')}
          />
        );

      // How we can help, Yes tapped a beat after it opens: their reply, the
      // dots, then what we'd need. For watching the beat before a reply.
      case 'help-how-yes':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('how-we-help')}
            autoMoveOn='Yes'
            onExit={() => setFrame('youre-in')}
          />
        );

      // The first question, recording a beat after it opens, so the prompts
      // can be watched ticking off as they talk.
      case 'help-ask-starts-recording':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('help')}
            autoRecord
            onExit={() => setFrame('youre-in')}
          />
        );

      // Handing it over, then I'm done tapped a beat after it opens, so the
      // pinned card can be watched letting go.
      case 'help-gather-done':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('gather')}
            sent={3}
            autoMoveOn="I'm done"
            onExit={() => setFrame('youre-in')}
          />
        );

      // Dev, after I'm done: what everything they sent came to, said back in a
      // message before what to start with.
      case 'help-collected':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('collected')}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The same, with the pencil tapped: the card's lines in the composer to
      // correct, and a bar above saying so.
      case 'help-collected-editing':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('collected')}
            editingCardOpen="Here's what we've got"
            onExit={() => setFrame('youre-in')}
          />
        );

      // Dev, once what we've got is confirmed: what we'd still need, with Do it
      // later to move on without it.
      case 'help-still-need':
        return (
          <ChatV2
            {...chat}
            questions={INTAKE_CHAT_V2_0_DYNAMIC}
            startAt={dynamicStep('still-need')}
            onExit={() => setFrame('youre-in')}
          />
        );

      // 2.0, after I'm done: how far it gets us, and what to start with.
      case 'help-start':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('start')}
            onExit={() => setFrame('youre-in')}
          />
        );

      // Before where to start: a number to reach them on, with Do this later
      // to leave it for now.
      case 'help-phone':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('phone')}
            onExit={() => setFrame('youre-in')}
          />
        );

      // What to start with, marking an answer a beat after it opens, so the
      // menu can be watched going down to it. Nothing is sent and the chat
      // stays put.
      case 'help-start-picking':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('start')}
            // The 2.0 design's, on the Interactions page. Dev's is
            // help-start-talk.
            autoPick='Claiming Attendance Allowance'
            onExit={() => setFrame('youre-in')}
          />
        );

      // Dev's close: what they could get, then into the app.
      case 'help-eligible':
        return (
          <ChatV2
            {...chat}
            questions={INTAKE_CHAT_V2_0_DYNAMIC}
            startAt={dynamicStep('eligible')}
            onExit={() => setFrame('youre-in')}
          />
        );

      // Off the main path, each opened where its branch turns.
      case 'help-not-quite':
      case 'help-summary-fixed':
      case 'help-how-scotland':
      case 'help-how-younger':
      case 'help-how-no-daily-help':
      case 'help-own-words-answered':
      case 'help-not-now':
      case 'help-later':
      case 'help-under-18':
      case 'help-phone-later':
      case 'help-need-help-first-summary':
      case 'help-need-help':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step(BRANCH_STEP[frame]!)}
            onExit={() => setFrame('youre-in')}
          />
        );

      // What they most want help with, sent and being read, as their first
      // answer was.
      case 'help-need-help-reading':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('need-help')}
            replied='Money Mum might be owed, and care at home.'
            reading
            onExit={() => setFrame('youre-in')}
          />
        );

      // The summary back with it in, to check again before where to start.
      case 'help-need-help-summary':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('summary-need')}
            onExit={() => setFrame('youre-in')}
          />
        );

      // Where to start with Book a call marked a beat after it
      // opens: the way into booking a call. Nothing is sent.
      case 'help-start-talk':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('start')}
            autoPick={BOOK_CALL}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The first answer typed rather than said, being read.
      case 'help-typed':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('help')}
            replied={HELP_TYPED}
            reading
            onExit={() => setFrame('youre-in')}
          />
        );

      // The first answer as a photo of a letter, being read.
      case 'help-photo':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('help')}
            uploaded='photo'
            reading
            onExit={() => setFrame('youre-in')}
          />
        );

      // A voice note that did not leave the phone.
      case 'help-fail-send-voice':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('help')}
            sent={1}
            sendFailed
            onExit={() => setFrame('youre-in')}
          />
        );

      // A typed answer that did not leave the phone.
      case 'help-fail-send':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('help')}
            replied={HELP_TYPED}
            sendFailed
            onExit={() => setFrame('youre-in')}
          />
        );

      // A photo of a letter that did not upload, sent with the first answer:
      // the one place on the main path they send one.
      case 'help-fail-upload':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('help')}
            uploaded='photo'
            uploadState='failed'
            onExit={() => setFrame('youre-in')}
          />
        );

      // The mic tapped without permission to use it.
      case 'help-mic-blocked':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('help')}
            pushback={HELP_STATES.micBlocked}
            onExit={() => setFrame('youre-in')}
          />
        );

      // A voice note the reading could not make out.
      case 'help-not-understood':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('help')}
            sent={1}
            pushback={HELP_STATES.notUnderstood}
            onExit={() => setFrame('youre-in')}
          />
        );

      // An answer too short to read anything from.
      case 'help-too-short':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('help')}
            replied='My mum'
            pushback={HELP_STATES.tooShort}
            onExit={() => setFrame('youre-in')}
          />
        );

      // Back after leaving partway, picked up where they were: here, at the
      // phone number.
      case 'help-resumed':
        return (
          <ChatV2
            {...chat}
            questions={chatQs}
            startAt={step('location')}
            pushback={HELP_STATES.resumed}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The dynamic concept, I'll do this later picked on what to start with.
      case 'help-start-later':
        return (
          <ChatV2
            {...chat}
            questions={INTAKE_CHAT_V2_0_LATER}
            startAt={intakeStepIn(INTAKE_CHAT_V2_0_LATER, 'later-start')}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The dynamic concept's Home, opening the app again: a new chat with
      // the logo and the greeting in the middle. The app's bar with a menu
      // where its logo was, opening the conversations; no tab bar, and 2.0's
      // own composer to talk or type.
      case 'home-conversations':
      // The same, with the menu tapped: the conversations drawer open.
      case 'home-conversations-menu':
        return (
          <>
            <ChatV2
              {...chat}
              questions={INTAKE_CHAT_V2_0_HOME}
              greeting={HOME_GREETING}
              // The person icon rather than their initials, on this concept.
              account={{
                ...app,
                initials: undefined,
                onMenu: () => setHistoryOpen(true),
              }}
              onExit={() => setFrame('youre-in')}
            />
            {historyOpen && (
              <ChatHistoryDrawer onClose={() => setHistoryOpen(false)} />
            )}
          </>
        );

      // Home, concept 2: upcoming calls and the conversations, on the page.
      case 'home-concept-2':
        return (
          <ChatV2
            {...chat}
            questions={INTAKE_CHAT_V2_0_HOME}
            emptyState={<HomeConcept2 />}
            account={{ ...app, initials: undefined }}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The same with what onboarding still needs as its checklist, and the
      // conversations back in the bar's menu rather than on the page.
      case 'home-concept-2-todo':
      // Concept 3.1: typing on it, then Keni ticking off what it can tell
      // was sent, or asking when it cannot and putting the list away.
      case 'home-c3-typing':
      case 'home-c3-sent':
      case 'home-c3-unsure':
      case 'home-c3-put-away':
        return (
          <>
            <ChatV2
              {...chat}
              {...HOME_C3[frame]!}
              emptyState={<HomeConcept2 todo conversations={false} />}
              account={{
                ...app,
                initials: undefined,
                onMenu: () => setHistoryOpen(true),
              }}
              onExit={() => setFrame('youre-in')}
            />
            {historyOpen && (
              <ChatHistoryDrawer onClose={() => setHistoryOpen(false)} />
            )}
          </>
        );

      // Concept 4: what she could get, with how far along each is, in place
      // of concept 3's list. Attendance Allowance opens a conversation about it.
      case 'home-c4':
      // The same Home, with a button to ask something where the chat's bar was.
      case 'home-c4-ask':
      case 'home-c4-ask-lime':
        return (
          <>
            <ChatV2
              {...chat}
              questions={INTAKE_CHAT_V2_0_HOME}
              askButton={
                frame === 'home-c4-ask'
                  ? { label: 'Ask me something', tone: 'coral' }
                  : frame === 'home-c4-ask-lime'
                    ? { label: 'Ask me something', tone: 'lime' }
                    : undefined
              }
              emptyState={
                <HomeConcept2
                  eligible
                  conversations={false}
                  onEligible={(title) => {
                    if (title === 'Attendance Allowance')
                      setFrame('home-c4-aa');
                    // The calculator is a conversation already: straight in,
                    // and back here when it is left.
                    if (title === 'Benefits calculator') {
                      setCalcHome('home-c4');
                      setFrame('calc-paperwork');
                    }
                  }}
                />
              }
              account={{
                ...app,
                initials: undefined,
                onMenu: () => setHistoryOpen(true),
              }}
              onExit={() => setFrame('youre-in')}
            />
            {historyOpen && (
              <ChatHistoryDrawer onClose={() => setHistoryOpen(false)} />
            )}
          </>
        );

      // Concept 4.1: Attendance Allowance's own conversation. What it is,
      // then what is still needed for it, pinned while they send it. The X
      // goes back to Home.
      case 'home-c4-aa':
      // The same, once they have talked about a part nobody had started.
      case 'home-c4-aa-part':
      // And with All tapped on its card, back to every part.
      case 'home-c4-aa-all':
        return (
          <ChatV2
            {...chat}
            questions={
              frame === 'home-c4-aa-all'
                ? INTAKE_CHAT_V2_0_AA_ALL
                : INTAKE_CHAT_V2_0_AA
            }
            startAt={frame === 'home-c4-aa' ? 0 : 1}
            account={{ ...app, initials: undefined }}
            onClose={() => setFrame('home-c4')}
            onComplete={() => setFrame('home-c4')}
            onExit={() => setFrame('youre-in')}
          />
        );

      // The benefits calculator, from Home's card. The same screens and
      // questions the benefits calculator flows page draws, here to click
      // through.
      case 'calc-start':
        return (
          <CarriedScreen
            onStart={() => setFrame('calc-paperwork')}
            bar={calcBar}
          />
        );

      case 'calc-paperwork':
        return (
          <ChatV2
            questions={CALCULATOR_CHAT}
            keepAnswers={CALCULATOR_KEEP_ANSWERS}
            bar={calcBar}
            onExit={() => setFrame(calcHome)}
            onReplay={onReplay}
            onQuestionChange={(id) => {
              onChatQuestion?.(id);
              if (id === 'processing') setCalcReading(true);
            }}
          />
        );

      case 'calc-read':
        return (
          <DocumentsReadScreen
            onContinue={() => setFrame('calc-questions')}
            bar={calcBar}
          />
        );

      case 'calc-questions':
        return (
          <ChatV2
            questions={CALCULATOR_CHAT}
            startAt={CALC_QUESTIONS_AT}
            keepAnswers={CALCULATOR_KEEP_ANSWERS}
            bar={calcBar}
            onExit={() => setFrame(calcHome)}
            onComplete={() => setFrame('calc-result')}
            onReplay={onReplay}
            onQuestionChange={onChatQuestion}
          />
        );

      case 'calc-result':
        return (
          <ResultScreen
            onStart={() => setFrame('book-call')}
            onTalk={() => setFrame('calc-start')}
            bar={calcBar}
          />
        );

      // The fork after the review: money or care.
      case 'choose-next':
        return (
          <ChooseNextScreen
            account={app}
            onCalculator={() => setFrame('calculator')}
          />
        );

      // The claims list, with how much of each we filled in. Kept for the
      // calculator, which is where a list of claims and their progress belongs.
      case 'filled':
        return (
          <FilledScreen
            account={app}
            onCalculator={() => setFrame('calculator')}
            onNext={() => setFrame('intake-actions')}
          />
        );

      case 'calculator':
        return <CalculatorScreen onNext={() => setFrame('eligible')} />;

      case 'eligible':
        return <EligibleScreen onNext={() => setFrame('review')} />;

      case 'review':
        return (
          <ReviewScreen
            account={app}
            onComplete={() => setFrame('book-call')}
          />
        );

      case 'book-call':
        // Booked or skipped, the next thing either way is the profile it all
        // landed in.
        return (
          <BookCallScreen
            account={app}
            onBook={() => setFrame('care-profile')}
            onSkip={() => setFrame('care-profile')}
          />
        );

      case 'review-all':
        return (
          <ReviewAllScreen
            account={app}
            onDone={() => setFrame('care-profile')}
          />
        );

      // Checking what we read, in the flow. One list, no chrome, one way on, and
      // then what it filled in.
      case 'review-facts':
        return (
          <ReviewFactsScreen
            account={review}
            // Straight into the app rather than asking what they would like to do
            // next: the calculator is a card on Home, so the question answers
            // itself by being there. choose-next is the version that asked, kept
            // as its own scene rather than on the way through.
            onComplete={() => setFrame(calcHome)}
          />
        );

      // The same step framed as what we kept: what their words turned into
      // first, then the gaps. Being tried against review-facts.
      case 'review-facts-kept':
        return (
          <ReviewFactsScreen
            account={review}
            kept
            onComplete={() => setFrame('home')}
          />
        );

      // The summary being fixed in place, after the pencil on its card.
      case 'summary-editing':
        return (
          <SummaryScreen
            account={review}
            {...(warm ? { copy: INTAKE_SUMMARY_V1_1 } : {})}
            editing
            onConfirm={() => setFrame('review-facts')}
            onCorrect={() => setFrame('chat-v2-again')}
          />
        );

      // What we heard, read back. The first thing the ping opens.
      case 'summary':
        return (
          <SummaryScreen
            account={review}
            {...(warm ? { copy: INTAKE_SUMMARY_V1_1 } : {})}
            onConfirm={() => setFrame('review-facts')}
            onCorrect={() => setFrame('chat-v2-again')}
          />
        );

      // The same screen when the reading did not work out: what we did get, with
      // the choice to carry on with it or say it again.
      case 'summary-problem':
        return (
          <SummaryScreen
            account={review}
            {...(warm ? { copy: INTAKE_SUMMARY_V1_1 } : {})}
            problem
            onConfirm={() => setFrame('review-facts')}
            onCorrect={() => setFrame('chat-v2-again')}
          />
        );

      // Back in the conversation to put something right. Not the intake again: it
      // opens over the summary, so it is one question, with a way out. It ends
      // where the intake ended, so the summary comes round a second time.
      // The correction given, so the row can show the question answered rather
      // than only asked.
      case 'chat-v2-again-answered':
        return (
          <ChatV2
            {...chat}
            questions={INTAKE_CHAT_AGAIN}
            fresh
            replied='It is my mum I look after, not my mother-in-law.'
            onClose={() => setFrame('summary')}
            onComplete={doneChatting}
            onExit={() => setFrame('summary')}
          />
        );

      case 'chat-v2-again':
        return (
          <ChatV2
            {...chat}
            // Its own short script: what we hold read back, then what is wrong
            // with it, then anything else. The intake's own questions are not
            // these — they ask for things already answered.
            questions={INTAKE_CHAT_AGAIN}
            fresh
            onClose={() => setFrame('summary')}
            onComplete={doneChatting}
            onExit={() => setFrame('summary')}
          />
        );

      // What editing each kind of answer looks like: typing over something we
      // read, filling a gap, picking between two sources that disagree, and
      // replacing something that has gone out of date.
      case 'review-facts-read':
        return <ReviewFactsScreen account={review} editing='dob' />;

      case 'review-facts-gap':
        return <ReviewFactsScreen account={review} editing='ni' />;

      case 'review-facts-conflict':
        return <ReviewFactsScreen account={review} editing='falls' />;

      case 'review-facts-stale':
        return <ReviewFactsScreen account={review} editing='address' />;

      // The other way in. Whatever they did not do in the flow waits here.
      case 'intake-actions':
        return <IntakeActionsScreen />;

      // Between the intake filling it and the review confirming it: the facts we
      // read are there and nobody has checked them.
      case 'care-profile-unreviewed':
        return <CareProfileScreen account={app} reviewed={false} />;

      // Arriving on it from the notification, with the sheet still up.
      case 'care-profile-arriving':
        return <CareProfileScreen account={app} reviewed={false} intro />;

      case 'care-profile':
        return <CareProfileScreen account={app} />;

      default:
        return (
          <LegalNameScreen
            account={account}
            onNext={() => setFrame('review-all')}
            onBack={() => setFrame('chat')}
          />
        );
    }
  };

  // 2.0 has its own style sheet; every other version keeps the intake's.
  return (
    <IntakeStyleContext.Provider
      value={is20 ? ONBOARDING_2_0_STYLE : INTAKE_STYLE}
    >
      {/* Keyed by when this module loaded, so an edit to Flow or its copy
          reopens the screen. A chat builds its messages once when it opens,
          and hot reload keeps them, so new words would not show otherwise. */}
      <Fragment key={LOADED_AT}>{screen()}</Fragment>
    </IntakeStyleContext.Provider>
  );
};

export default Flow;
